// Copied verbatim from apps/web/src/libs/ui/components/Icon/Icon.tsx.
// Keep the two in sync by hand until the DS components move into packages/ui.
import { cn } from "logic/utils";

import { ICON_MAP } from "./icon-map";

/** All icon names — snake_case, mirroring the Figma variant names 1:1. */
export type IconName = keyof typeof ICON_MAP;

/** Only the sizes that actually exist for icon `N` (type-level enforcement). */
type SizeFor<N extends IconName> = keyof (typeof ICON_MAP)[N] & ("default" | "small");

const SIZE_CLASS = { default: "size-5", small: "size-4" } as const; // 20px / 16px

export interface IconProps<N extends IconName> {
  /** Icon name. @see Figma 20px https://www.figma.com/design/hCI9wopUpt49KQ7bWrDGuB/?node-id=262-2563 */
  name: N;
  /**
   * `default` = 20px (icon-20), `small` = 16px (icon-16). Only sizes the icon
   * has are allowed — passing a missing size is a compile error.
   * @default "default"
   */
  size?: SizeFor<N>;
  /** Extra classes. Color via `text-*` (icon uses `currentColor`); may override size. */
  className?: string;
  /** Accessible label. Omit for decorative icons (renders `aria-hidden`). */
  title?: string;
}

/**
 * Monochrome UI glyph from the Design System icon library. Inherits color via
 * `currentColor`, so set color on a parent (`<span className="text-teal-600">`)
 * or pass `className`. Renders the generated `public/svg/ds-icons` file inline via
 * `<use>`, so it recolors correctly (unlike an `<img>`).
 *
 * @see Figma 20px — https://www.figma.com/design/hCI9wopUpt49KQ7bWrDGuB/?node-id=262-2563
 * @see Figma 16px — https://www.figma.com/design/hCI9wopUpt49KQ7bWrDGuB/?node-id=283-18872
 *
 * @example Basic (20px, inherits text color)
 * ```tsx
 * <Icon name="caret_down" />
 * ```
 * @example Small (16px) + recolor
 * ```tsx
 * <span className="text-teal-600"><Icon name="lock" size="small" /></span>
 * ```
 * @example Labelled (non-decorative)
 * ```tsx
 * <Icon name="search" title="Search" />
 * ```
 */
export function Icon<N extends IconName>({ name, size, className, title }: IconProps<N>) {
  const map = ICON_MAP[name] as Record<string, string>;
  const resolved = size ?? ("default" in map ? "default" : "small");

  return (
    <svg
      aria-hidden={title ? undefined : true}
      className={cn(SIZE_CLASS[resolved], className)}
      fill="currentColor"
      focusable="false"
      role={title ? "img" : undefined}
    >
      {title ? <title>{title}</title> : null}
      <use href={`${map[resolved]}#i`} />
    </svg>
  );
}
